import { EventEmitter, Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, DocumentReference } from '@angular/fire/compat/firestore';
import { Wallet } from '@app/@shared/models/wallet.model';
import { CredentialsService } from '@app/auth';
import { WalletApiService } from './wallet-api.service';
import { WalletTypeService } from './wallet-type.service';
import { Logger } from '../logger.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const log = new Logger('wallet-service');

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private collection: CollectionReference<Wallet>;
  balanceChange = new EventEmitter<number>();

  constructor(
    private firestore: AngularFirestore,
    private credentialsS: CredentialsService,
    private walletApiS: WalletApiService,
    private walletTypeS: WalletTypeService
  ) {
    this.collection = this.firestore.collection<Wallet>('wallets').ref;
  }

  // Obtener todos los elementos
  async getAll(): Promise<Wallet[]> {
    const snap = await this.collection.get();
    const data = snap.docs.map((o) => {
      return { ...o.data(), id: o.id };
    });
    return data;
  }

  // Obtener un elemento por ID
  async getById(id: string): Promise<Wallet | null> {
    const snap = await this.collection.doc(id).get();
    if (snap.exists) {
      const data = { id: snap.id, ...snap.data() } as Wallet;
      return data;
    } else {
      return null;
    }
  }

  // Crear un nuevo elemento
  async create(item: Wallet): Promise<DocumentReference<Wallet>> {
    return await this.collection.add(item);
  }

  // Actualizar un elemento
  async update(id: string, item: Partial<Wallet>): Promise<void> {
    await this.collection.doc(id).update(item);
  }

  // Eliminar un elemento
  async delete(id: string): Promise<void> {
    await this.collection.doc(id).delete();
  }

  async getBalance() {
    const credentials = this.credentialsS.credentials;
    if (!credentials) {
      return {
        balance: 0,
        email: '',
      };
    }
    const { id, email } = credentials;
    const wallets = await this.getByCustomerId(id);
    if (wallets.length) {
      const balance = wallets.reduce((a, b) => a + b.balance, 0);
      log.info(balance);
      this.balanceChange.emit(balance);
      return {
        balance,
        email,
      };
    } else {
      const walletTypes = await this.walletTypeS.getAll();
      const loteria = walletTypes.find((w) => w.walletName === 'loteria');
      await this.walletApiS.createWallet({
        walletTypeId: loteria?.uid!,
        currency: 'MXN',
      });
      return {
        balance: 0,
        email,
      };
    }
  }

  async getByCustomerId(id: string): Promise<Wallet[]> {
    const ref = this.getRef(id, 'customers');
    const snap = await this.collection.where('customerId', '==', ref).get();
    const data = snap.docs.map((o) => {
      return { ...o.data(), id: o.id };
    });
    return data;
  }

  getRef(id: string, collection: string) {
    return this.firestore.collection(collection).doc(id).ref;
  }

  /**
   * Suscripción en tiempo real a los cambios en las wallets del cliente
   * Actualiza automáticamente el balance cuando cambian las wallets
   * @param customerId ID del cliente
   * @returns Observable que emite el balance total y el email
   */
  subscribeToBalance(customerId: string): Observable<{ balance: number; email: string }> {
    const customerRef = this.getRef(customerId, 'customers');

    return this.firestore
      .collection<Wallet>('wallets', (ref) => ref.where('customerId', '==', customerRef))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((wallets) => {
          const balance = wallets.length > 0 ? wallets.reduce((a, b) => a + (b.balance || 0), 0) : 0;

          const email = this.credentialsS.credentials?.email || '';

          // Emitir el cambio de balance
          this.balanceChange.emit(balance);

          log.info('Balance actualizado en tiempo real:', balance);

          return { balance, email };
        })
      );
  }

  async redeemCoupon(code: string) {
    return await this.walletApiS.redeemCoupon(code);
  }
}
